import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, SyncButton, ResponsiveTableWrap, AdminEmptyState, slugify } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MediaField } from "@/components/admin/media-picker";
import { AiAssistButton } from "@/components/ai/ai-assist-button";
import { ImportDefaultsButton } from "@/components/admin/import-defaults-button";
import { buildPostSeeds } from "@/lib/admin-seeds";

export const Route = createFileRoute("/_authenticated/admin/posts")({
  component: PostsAdmin,
});

type Post = {
  id: string; title: string; slug: string; excerpt: string | null;
  content: string; cover_url: string | null; category_id: string | null;
  seo_title: string | null; seo_description: string | null; seo_score: number;
  published: boolean; updated_at: string;
};

function computeSeoScore(p: { title: string; excerpt: string; content: string; seo_title: string; seo_description: string }) {
  let score = 0;
  if (p.title.length >= 20 && p.title.length <= 70) score += 20;
  if (p.seo_title && p.seo_title.length <= 60) score += 20;
  if (p.seo_description && p.seo_description.length >= 80 && p.seo_description.length <= 160) score += 20;
  if (p.excerpt && p.excerpt.length >= 60) score += 15;
  if (p.content.length >= 300) score += 25;
  return Math.min(100, score);
}

function PostsAdmin() {
  const { data: posts, isLoading, refetch, isFetching } = useResourceList<Post>("posts", "updated_at");
  const { create, update, remove } = useResourceMutations("posts");
  const [editing, setEditing] = useState<Post | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "published" | "draft">("all");

  const duplicatePost = (p: Post) => {
    const base = slugify(p.title + " copy");
    const existing = new Set((posts ?? []).map((x) => x.slug));
    let slug = base;
    let i = 2;
    while (existing.has(slug)) slug = `${base}-${i++}`;
    create.mutate({
      title: `${p.title} (Copy)`,
      slug,
      excerpt: p.excerpt,
      content: p.content,
      cover_url: p.cover_url,
      category_id: p.category_id,
      seo_title: p.seo_title,
      seo_description: p.seo_description,
      seo_score: p.seo_score,
      published: false,
      published_at: null,
    });
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (posts ?? []).filter((p) => {
      if (filter === "published" && !p.published) return false;
      if (filter === "draft" && p.published) return false;
      if (!q) return true;
      return [p.title, p.slug, p.excerpt].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [posts, search, filter]);

  const tabs: { id: typeof filter; label: string }[] = [
    { id: "all", label: `All (${posts?.length ?? 0})` },
    { id: "published", label: `Live (${posts?.filter((p) => p.published).length ?? 0})` },
    { id: "draft", label: `Drafts (${posts?.filter((p) => !p.published).length ?? 0})` },
  ];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Blog posts"
        description="Write, categorize and publish articles. SEO score is computed automatically."
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New post"
        actions={<><SyncButton onSync={() => refetch()} loading={isFetching} /><ImportDefaultsButton table="posts" onConflict="slug" loadRows={buildPostSeeds} /></>}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search posts…"
        count={rows.length}
      />

      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <Button key={t.id} size="sm" variant={filter === t.id ? "default" : "outline"} onClick={() => setFilter(t.id)}>
            {t.label}
          </Button>
        ))}
      </div>

      {!isLoading && !rows.length ? (
        <AdminEmptyState title={search || filter !== "all" ? "No posts match" : "No posts yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden sm:table-cell">SEO</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
                {rows.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium">{p.title}</div>
                      <div className="text-xs text-muted-foreground">
                        /{p.slug}
                        <span className="sm:hidden"> · SEO <span className={p.seo_score >= 70 ? "text-emerald-500" : p.seo_score >= 40 ? "text-amber-500" : "text-destructive"}>{p.seo_score}</span></span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className={p.seo_score >= 70 ? "text-emerald-500" : p.seo_score >= 40 ? "text-amber-500" : "text-destructive"}>
                        {p.seo_score}/100
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.published ? "default" : "secondary"}>{p.published ? "Published" : "Draft"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions
                        onEdit={() => { setEditing(p); setOpen(true); }}
                        onDuplicate={() => duplicatePost(p)}
                        onDelete={() => { if (confirm(`Delete "${p.title}"?`)) remove.mutate(p.id); }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}

      <PostDialog
        key={editing?.id ?? "new"}
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        onSubmit={(values) => {
          if (editing) update.mutate({ id: editing.id, values }, { onSuccess: () => setOpen(false) });
          else create.mutate(values, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function PostDialog({
  open, onOpenChange, editing, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Post | null;
  onSubmit: (values: any) => void;
}) {
  const [title, setTitle] = useState(editing?.title ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [excerpt, setExcerpt] = useState(editing?.excerpt ?? "");
  const [content, setContent] = useState(editing?.content ?? "");
  const [cover, setCover] = useState(editing?.cover_url ?? "");
  const [seoTitle, setSeoTitle] = useState(editing?.seo_title ?? "");
  const [seoDesc, setSeoDesc] = useState(editing?.seo_description ?? "");
  const [published, setPublished] = useState(editing?.published ?? false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    setScore(computeSeoScore({ title, excerpt, content, seo_title: seoTitle, seo_description: seoDesc }));
  }, [title, excerpt, content, seoTitle, seoDesc]);

  const handleSubmit = () => {
    onSubmit({
      title, slug: slug || slugify(title),
      excerpt: excerpt || null, content, cover_url: cover || null,
      seo_title: seoTitle || null, seo_description: seoDesc || null,
      seo_score: score, published,
      published_at: published ? (editing?.published ? undefined : new Date().toISOString()) : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={slugify(title)} /></div>
          <MediaField label="Cover image" value={cover} onChange={(url) => setCover(url ?? "")} />
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label>Excerpt</Label>
              <AiAssistButton task="summarize" context={content} defaultPrompt="Write a 1-2 sentence excerpt for this post." onApply={setExcerpt} label="AI excerpt" />
            </div>
            <Textarea rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label>Content (Markdown)</Label>
              <AiAssistButton task="blog_post" context={content} defaultPrompt={title ? `Write a blog post titled: ${title}` : "Write a blog post about…"} onApply={setContent} />
            </div>
            <Textarea rows={8} value={content} onChange={(e) => setContent(e.target.value)} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2"><Label>SEO title</Label><Input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} /></div>
            <div className="grid gap-2"><Label>SEO description</Label><Input value={seoDesc} onChange={(e) => setSeoDesc(e.target.value)} /></div>
          </div>
          <AiAssistButton
            task="seo_meta"
            context={`Title: ${title}\n\n${content}`}
            defaultPrompt="Generate SEO title and description for this post."
            label="Auto-fill SEO meta"
            onApply={(text) => {
              try {
                const clean = text.replace(/```json|```/g, "").trim();
                const json = JSON.parse(clean);
                if (json.title) setSeoTitle(String(json.title).slice(0, 60));
                if (json.description) setSeoDesc(String(json.description).slice(0, 160));
              } catch { /* user can paste manually */ }
            }}
          />
          <div className="rounded-md border border-border p-3 text-sm">
            SEO score: <span className={score >= 70 ? "text-emerald-500" : score >= 40 ? "text-amber-500" : "text-destructive"}>{score}/100</span>
          </div>
          <div className="flex items-center gap-3"><Switch checked={published} onCheckedChange={setPublished} /><Label>Published</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
